import MainClass, { MAIN_CLASS_PARAMS } from '../utils/mainClass.ts';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

class CtaDouble extends MainClass {
	private isMobile: boolean = window.matchMedia( '(max-width: 904px)' ).matches; 
	constructor( options: MAIN_CLASS_PARAMS ) {
		super( options );
		this.selector && this.init();
	}

	private rightToLeftHoverAnimation = ( element: HTMLElement ) => {
		const xValue = this.isMobile ? '-6%' : '-15%';
		return gsap.fromTo(
			element,
			{
				x: 0,
			},
			{
				x: xValue,
				duration: 1.3,
				repeat: -1,
				yoyoEase: true,
				ease: 'power1.inOut',
			},
		);
	};
	private leftToRightHoverAnimation = ( element: HTMLElement ) => {
		const xValue = this.isMobile ? '6%' : '15%';
		return gsap.fromTo(
			element,
			{
				x: 0,
			},
			{
				x: xValue,
				duration: 1.3,
				repeat: -1,
				yoyoEase: true,
				ease: 'power1.inOut',
			},
		);
	};

	private scaleUpHoverAnimation = ( element: HTMLElement ) => {
		const sclaleValue = this.isMobile ? 1.1 : 1.2;
		return gsap.fromTo(
			element,
			{
				scale: 1,
			},
			{
				scale: sclaleValue,
				duration: 1.3,
				repeat: -1,
				yoyoEase: true,
				ease: 'power1.inOut',
			},
		);
	};
	private scaleDownHoverAnimation = ( element: HTMLElement ) => {
		return gsap.fromTo(
			element,
			{
				scale: 1,
			},
			{
				scale: 0.9,
				duration: 1.3,
				repeat: -1,
				yoyoEase: true,
				ease: 'power1.inOut',
			},
		);
	};
	private topToDownHoverAnimation = ( element: HTMLElement ) => {
		return gsap.fromTo(
			element,
			{
				y: 0,
			},
			{
				y: '15%',
				duration: 1.3,
				repeat: -1,
				yoyoEase: true,
				ease: 'power1.inOut',
			},
		);
	};

	private initAnimation = ( shapes, contents, callback ) => {
		gsap.registerPlugin( ScrollTrigger );

		const TIMELINE_ANIMATION = gsap.timeline( {
			scrollTrigger: {
				trigger: this.selector.childNodes[0],
				start: 'top +=60%',
				end: 'top +=30%',
			}
		} );

		// Handle animation for amber theme
		if ( shapes.amber.leftBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.amber.leftBlockshape1,
				{
					x: '-100%',
					opacity:0,
				},
				{
					opacity:1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);

			TIMELINE_ANIMATION.fromTo(
				shapes.amber.leftBlockshape2,
				{
					x: '-200%',
				},
				{
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);

			TIMELINE_ANIMATION.fromTo(
				shapes.amber.rightBlockshape1,
				{
					x: '-200%',
				},
				{
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);

			TIMELINE_ANIMATION.fromTo(
				shapes.amber.rightBlockshape2,
				{
					rotation: 90,
					transformOrigin:"center right",
				},
				{
					transformOrigin:"center right",
					rotation: 0,
					duration: 1,
					ease: 'power1.inOut',
				},
				'start',
			);
		}

		// Handle animation for violet theme
		if ( shapes.violet.leftBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.violet.leftBlockshape1,
				{
					rotation:270
				},
				{
					rotation:0,
					transformOrigin:"bottom right",
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.violet.leftBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.violet.leftBlockshape2,
				{
					x: '-30%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.violet.rightBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.violet.rightBlockshape1,
				{
					opacity: 0,
					x: '30%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.violet.rightBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.violet.rightBlockshape2,
				{
					rotate: 90,
					opacity: 0,
					x: '30%',
				},
				{
					rotate: 0,
					opacity: 1,
					x: 0,
					duration: 1,
					ease: 'power1.inOut',
				},
				'start',
			);
		}

		// Handle animation for sand theme
		if ( shapes.sand.leftBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.sand.leftBlockshape1,
				{
					opacity: 0,
					scale: 2,
				},
				{
					opacity: 1,
					scale: 1,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.sand.rightBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.sand.rightBlockshape1,
				{
					opacity: 0,
					rotate: -90,
				},
				{
					opacity: 1,
					rotate: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.sand.rightBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.sand.rightBlockshape2,
				{
					opacity: 0,
					rotate: 90,
				},
				{
					opacity: 1,
					rotate: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		// Handle animation for orange theme

		if ( shapes.orange.leftBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.orange.leftBlockshape1,
				{
					opacity: 0,
					x: '-100%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.orange.leftBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.orange.leftBlockshape2,
				{
					opacity: 0,
					x: '-50%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.orange.rightBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.orange.rightBlockshape1,
				{
					opacity: 0,
					x: '-100%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}
		if ( shapes.orange.rightBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.orange.rightBlockshape2,
				{
					opacity: 0,
					x: '100%',
				},
				{
					opacity: 1,
					x: 0,
					duration: 0.8,
					ease: 'power1.inOut',
				},
				'start',
			);
		}

		// Handle animation for purple theme
		if ( shapes.purple.leftBlockshape1 ) {
			TIMELINE_ANIMATION.set(shapes.purple.leftBlockshape1,{rotation: 270})
			TIMELINE_ANIMATION.set(shapes.purple?.rightBlockshape1,{rotation: 270})
			TIMELINE_ANIMATION.fromTo(
				shapes.purple.leftBlockshape1,
				{
					rotation: 270,
				},
				{
					rotation: 360,
					duration: 1,
					transformOrigin:"bottom right",
				},
				'start',
			);
		}
		if ( shapes.purple.rightBlockshape1 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.purple.rightBlockshape1,
				{
					rotation: 270,
				},
				{
					rotation: 360,
					transformOrigin:"bottom right",
					duration: 1,
				},
				'start',
			);
		}

		if ( shapes.purple.rightBlockshape2 ) {
			TIMELINE_ANIMATION.fromTo(
				shapes.purple.rightBlockshape2,
				{
					rotation: 180,
					top: '-100%',
					opacity: 0,
				},
				{
					rotation: 0,
					top: 'inherit',
					opacity: 1,
					duration: 1,
					transformOrigin:"center right"
				},
				'start',
			);
		}
		contents.forEach( ( item, index ) => {
			if ( item ) {
				TIMELINE_ANIMATION.fromTo(
					item,
					{
						opacity: 0,
						y: '150%',
					},
					{
						opacity: 1,
						y: 0,
						duration: 0.8,
						ease: 'power1.inOut',
						delay: index * 0.1,
					},
					'start',
				);
			}
		} );

		TIMELINE_ANIMATION.then(()=>{
			if(typeof callback === 'function'){
				callback()
			}
		})
	};

	private initHoverAnimation = ( shapes ) => {
		let argsGsapAnimate = []
		if ( shapes.purple.leftBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.purple.leftBlockshape1 ));
		}
		if ( shapes.purple.rightBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.purple.rightBlockshape1 ));
		}
		if ( shapes.purple.rightBlockshape2 ) {
			argsGsapAnimate.push(this.scaleUpHoverAnimation( shapes.purple.rightBlockshape2 ));
		}
		if ( shapes.amber.leftBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.amber.leftBlockshape1 ));
		}
		if ( shapes.amber.leftBlockshape2 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.amber.leftBlockshape2 ));
		}
		if ( shapes.amber.rightBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.amber.rightBlockshape1 ));
		}
		if ( shapes.amber.rightBlockshape2 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.amber.rightBlockshape2 ));
		}

		if ( shapes.violet.leftBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.violet.leftBlockshape1 ));
		}
		if ( shapes.violet.leftBlockshape2 ) {
			argsGsapAnimate.push(this.leftToRightHoverAnimation( shapes.violet.leftBlockshape2 ));
		}
		if ( shapes.violet.rightBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.violet.rightBlockshape1 ));
		}
		if ( shapes.violet.rightBlockshape2 ) {
			argsGsapAnimate.push(this.scaleUpHoverAnimation( shapes.violet.rightBlockshape2 ));
		}
		if ( shapes.orange.leftBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.orange.leftBlockshape1 ));
		}
		if ( shapes.orange.leftBlockshape2 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.orange.leftBlockshape2 ));
		}
		if ( shapes.orange.rightBlockshape1 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.orange.rightBlockshape1 ));
		}
		if ( shapes.orange.rightBlockshape2 ) {
			argsGsapAnimate.push(this.rightToLeftHoverAnimation( shapes.orange.rightBlockshape2 ));
		}
		if ( shapes.sand.leftBlockshape1 ) {
			argsGsapAnimate.push(this.scaleUpHoverAnimation( shapes.sand.leftBlockshape1 ));
		}
		if ( shapes.sand.rightBlockshape1 ) {
			argsGsapAnimate.push(this.topToDownHoverAnimation( shapes.sand.rightBlockshape1 ));
		}
		if ( shapes.sand.rightBlockshape2 ) {
			argsGsapAnimate.push(this.scaleDownHoverAnimation( shapes.sand.rightBlockshape2 ));
		}
		return argsGsapAnimate
	};

	init() {
		const amberTheme = this.selector.querySelector( '.theme-amber' ) as HTMLElement;
		const purpleTheme = this.selector.querySelector( '.theme-purple' ) as HTMLElement;
		const violetTheme = this.selector.querySelector( '.theme-violet' ) as HTMLElement;
		const sandTheme = this.selector.querySelector( '.theme-sand' ) as HTMLElement;
		const orangeTheme = this.selector.querySelector( '.theme-orange' ) as HTMLElement;
		// amber shape decors
		const amberLeftBlockshape1 = amberTheme?.querySelector( '.left-content .shape1' ) as HTMLElement;
		const amberLeftBlockshape2 = amberTheme?.querySelector( '.left-content .shape2' ) as HTMLElement;
		const amberRightBlockshape1 = amberTheme?.querySelector( '.right-content .shape1' ) as HTMLElement;
		const amberRightBlockshape2 = amberTheme?.querySelector( '.right-content .shape2' ) as HTMLElement;

		// violet shape decors
		const violetLeftBlockshape1 = violetTheme?.querySelector( '.left-content .shape1' ) as HTMLElement;
		const violetLeftBlockshape2 = violetTheme?.querySelector( '.left-content .shape2' ) as HTMLElement;
		const violetRightBlockshape1 = violetTheme?.querySelector( '.right-content .shape1' ) as HTMLElement;
		const violetRightBlockshape2 = violetTheme?.querySelector( '.right-content .shape2' ) as HTMLElement;

		// organe shape decors
		const orangeLeftBlockshape1 = orangeTheme?.querySelector( '.left-content .shape1' ) as HTMLElement;
		const orangeLeftBlockshape2 = orangeTheme?.querySelector( '.left-content .shape2' ) as HTMLElement;
		const orangeRightBlockshape1 = orangeTheme?.querySelector( '.right-content .shape1' ) as HTMLElement;
		const orangeRightBlockshape2 = orangeTheme?.querySelector( '.right-content .shape2' ) as HTMLElement;

		// sand shape decors
		const sandLeftBlockshape1 = sandTheme?.querySelector( '.left-content .shape1' ) as HTMLElement;
		const sandRightBlockshape1 = sandTheme?.querySelector( '.right-content .shape1' ) as HTMLElement;
		const sandRightBlockshape2 = sandTheme?.querySelector( '.right-content .shape2' ) as HTMLElement;

		// purple shape decors
		const purpleLeftBlockshape1 = purpleTheme?.querySelector( '.left-content .shape1' ) as HTMLElement;
		const purpleRightBlockshape1 = purpleTheme?.querySelector( '.right-content .shape1' ) as HTMLElement;
		const purpleRightBlockshape2 = purpleTheme?.querySelector( '.right-content .shape2' ) as HTMLElement;

		const shapes = {
			amber: {
				leftBlockshape1: amberLeftBlockshape1,
				leftBlockshape2: amberLeftBlockshape2,
				rightBlockshape1: amberRightBlockshape1,
				rightBlockshape2: amberRightBlockshape2,
			},
			violet: {
				leftBlockshape1: violetLeftBlockshape1,
				leftBlockshape2: violetLeftBlockshape2,
				rightBlockshape1: violetRightBlockshape1,
				rightBlockshape2: violetRightBlockshape2,
			},
			orange: {
				leftBlockshape1: orangeLeftBlockshape1,
				leftBlockshape2: orangeLeftBlockshape2,
				rightBlockshape1: orangeRightBlockshape1,
				rightBlockshape2: orangeRightBlockshape2,
			},
			sand: {
				leftBlockshape1: sandLeftBlockshape1,
				rightBlockshape1: sandRightBlockshape1,
				rightBlockshape2: sandRightBlockshape2,
			},
			purple: {
				leftBlockshape1: purpleLeftBlockshape1,
				rightBlockshape1: purpleRightBlockshape1,
				rightBlockshape2: purpleRightBlockshape2,
			},
		};

		const contentAnimationEles = [
			// amber
			amberTheme?.querySelector( '.left-content .cta-left-column-heading' ) as HTMLElement,
			amberTheme?.querySelector( '.left-content .cta-left-column-description' ) as HTMLElement,
			amberTheme?.querySelector( '.right-content .cta-right-column-heading' ) as HTMLElement,
			amberTheme?.querySelector( '.right-content .cta-right-column-description' ) as HTMLElement,
			amberTheme?.querySelector( '.left-content .cta-block-btn' ) as HTMLElement,
			amberTheme?.querySelector( '.right-content .cta-block-btn' ) as HTMLElement,
			// violet
			violetTheme?.querySelector( '.right-content .cta-right-column-heading' ) as HTMLElement,
			violetTheme?.querySelector( '.right-content .cta-right-column-description' ) as HTMLElement,
			violetTheme?.querySelector( '.left-content .cta-left-column-heading' ) as HTMLElement,
			violetTheme?.querySelector( '.left-content .cta-left-column-description' ) as HTMLElement,
			violetTheme?.querySelector( '.left-content .cta-block-btn' ) as HTMLElement,
			violetTheme?.querySelector( '.right-content .cta-block-btn' ) as HTMLElement,
			// orange
			orangeTheme?.querySelector( '.right-content .cta-right-column-heading' ) as HTMLElement,
			orangeTheme?.querySelector( '.right-content .cta-right-column-description' ) as HTMLElement,
			orangeTheme?.querySelector( '.left-content .cta-left-column-heading' ) as HTMLElement,
			orangeTheme?.querySelector( '.left-content .cta-left-column-description' ) as HTMLElement,
			orangeTheme?.querySelector( '.left-content .cta-block-btn' ) as HTMLElement,
			orangeTheme?.querySelector( '.right-content .cta-block-btn' ) as HTMLElement,
			// sand
			sandTheme?.querySelector( '.left-content .cta-left-column-heading' ) as HTMLElement,
			sandTheme?.querySelector( '.left-content .cta-left-column-description' ) as HTMLElement,
			sandTheme?.querySelector( '.left-content .cta-block-btn' ) as HTMLElement,
			sandTheme?.querySelector( '.right-content .cta-bfg-logo' ) as HTMLElement,
			sandTheme?.querySelector( '.right-content .cta-right-column-heading' ) as HTMLElement,
			sandTheme?.querySelector( '.right-content .cta-right-column-description' ) as HTMLElement,
			sandTheme?.querySelector( '.right-content .cta-block-btn' ) as HTMLElement,
			// purple
			purpleTheme?.querySelector( '.left-content .cta-left-column-heading' ) as HTMLElement,
			purpleTheme?.querySelector( '.left-content .cta-left-column-description' ) as HTMLElement,
			purpleTheme?.querySelector( '.left-content .cta-block-btn' ) as HTMLElement,
		];
		const contentAnimationElesFomatted = contentAnimationEles.filter( item => item ) as HTMLElement[];
		this.initAnimation( shapes, contentAnimationElesFomatted, ()=>{
			let flagInit = 0
			let getFunc = null
			if ( this.isMobile ) {
				this.selector.addEventListener( 'click', () => {
					if ( flagInit === 0 ) {
						getFunc = this.initHoverAnimation( shapes );
					}
					flagInit++;
					if ( flagInit % 2 === 0 ) {
						getFunc.forEach( item => item.pause() );
					} else {
						getFunc.forEach( item => item.play() );
					}
				} );
			} else {
				this.selector.addEventListener( 'mouseenter', () => {
					if ( flagInit === 0 ) {
						flagInit++;
						getFunc = this.initHoverAnimation( shapes );
						this.selector.addEventListener( 'mouseleave', () => {
							// Remove hover animation
							if ( getFunc.length > 0 ) {
								getFunc.forEach( item => item.pause() );
							}
						} );
					} else {
						if ( getFunc.length > 0 ) {
							this.selector.addEventListener( 'click', () => {} );
							getFunc.forEach( item => item.play() );
						}
					}
				} );
			}
		} );

	}
}

export default CtaDouble;
